import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import QuoteCarousel from '../components/QuoteCarousel';
import * as amplitude from '@amplitude/analytics-browser';

const Home = () => {
  const [wikiContent, setWikiContent] = useState('');
  const [wikiImage, setWikiImage] = useState('');
  const [isLightTheme, setIsLightTheme] = useState(false);

  useEffect(() => {
    // Check if light theme is active
    const appElement = document.getElementById('app-body');
    setIsLightTheme(appElement ? appElement.classList.contains('light-theme') : false);

    // Add event listener for theme changes
    const handleThemeChange = () => {
      const appElement = document.getElementById('app-body');
      setIsLightTheme(appElement ? appElement.classList.contains('light-theme') : false);
    };

    window.addEventListener('themechange', handleThemeChange);

    // Track page view with Amplitude
    amplitude.track('Page Viewed', {
      page: 'Home',
      theme: appElement ? (appElement.classList.contains('light-theme') ? 'light' : 'dark') : 'dark'
    });

    return () => {
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, []);

  useEffect(() => {
    // Fetch Wikipedia content about 3D printing
    const fetchWikiContent = async () => {
      try {
        // Wikipedia API endpoint for getting page content
        const contentEndpoint = "https://en.wikipedia.org/api/rest_v1/page/summary/3D_printing";
        const contentResponse = await axios.get(contentEndpoint);

        if (contentResponse.data && contentResponse.data.extract) {
          setWikiContent(contentResponse.data.extract);
        } else {
          setWikiContent("Content not found");
        }

        // Wikipedia API endpoint for getting page images
        const imageEndpoint = "https://en.wikipedia.org/w/api.php";
        const imageResponse = await axios.get(imageEndpoint, {
          params: {
            origin: "*",
            action: "query",
            format: "json",
            titles: "3D printing",
            prop: "pageimages",
            piprop: "original"
          }
        });

        if (imageResponse.data &&
            imageResponse.data.query &&
            imageResponse.data.query.pages) {
          const pages = imageResponse.data.query.pages;
          const page = Object.values(pages)[0];
          if (page && page.original && page.original.source) {
            setWikiImage(page.original.source);
          }
        }
      } catch (error) {
        console.error("Error fetching Wikipedia content:", error);
        setWikiContent("Error loading content");
      }
    };

    fetchWikiContent();
  }, []);

  // Smooth scrolling for anchor links
  useEffect(() => {
    const handleSmoothScroll = (e) => {
      e.preventDefault();
      const targetId = e.currentTarget.getAttribute('href').substring(1);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth'
        });
      }
    };

    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    anchorLinks.forEach(anchor => {
      anchor.addEventListener('click', handleSmoothScroll);
    });

    return () => {
      anchorLinks.forEach(anchor => {
        anchor.removeEventListener('click', handleSmoothScroll);
      });
    };
  }, []);

  return (
    <>
      {/* Hero Section */}
      <div className="card z-depth-2">
        <div className="card-image" style={{ maxHeight: '300px', overflow: 'hidden' }}>
          <img
            src="/images/jakub-zerdzicki-GBx1U5dMZNk-unsplash.jpg"
            alt="3D Printing"
            style={{ objectFit: 'cover', objectPosition: 'center', width: '100%' }}
          />
          <span className="card-title">
            <h1 className={isLightTheme ? "text-shadow" : "white-text text-shadow"} style={isLightTheme ? {color: '#333'} : {}}>Professional 3D Printing Services</h1>
            <h5 className={isLightTheme ? "text-shadow" : "white-text text-shadow"} style={isLightTheme ? {color: '#333'} : {}}>Bringing your ideas to life with precision and quality</h5>
          </span>
        </div>
        <div className="card-content">
          <p className="flow-text">
            {wikiContent || "We offer high-quality 3D printing services for prototypes, custom parts, artistic creations, and more. With cutting-edge technology and expert craftsmanship, we deliver exceptional results for all your 3D printing needs."}
          </p>
          <div className="center-align" style={{ marginTop: '30px' }}>
            <Link 
              to="/contact" 
              className="btn-large waves-effect waves-light"
              style={{ margin: '10px 15px' }}
              onClick={() => amplitude.track('Button Clicked', {
                button: 'Get a Quote',
                location: 'Home Hero Section'
              })}
            >
              Get a Quote
            </Link>
            <Link 
              to="/stl-generator" 
              className="btn-large waves-effect waves-light orange darken-2"
              style={{ margin: '10px 15px' }}
              onClick={() => amplitude.track('Button Clicked', {
                button: 'Generate Your Own STL',
                location: 'Home Hero Section'
              })}
            >
              Generate Your Own STL
            </Link>
          </div>
        </div>
      </div>

      {/* Quote Carousel */}
      <QuoteCarousel />

      {/* Services Section */}
      <div id="services" className="section">
        <h2 className="center-align">Our Services</h2>
        <div className="row">
          {/* Service 1 */}
          <div className="col s12 m6 l4">
            <div className="card hoverable service-card">
              <div className="card-image">
              <img src="/images/rapid-prototyping.jpg" alt="Rapid Prototyping" />
                <span className="card-title">Rapid Prototyping</span>
              </div>
              <div className="card-content">
                <p>Turn your ideas into physical prototypes quickly. Perfect for product development and testing.</p>
              </div>
              <div className="card-action">
                <Link to="/contact">Learn More</Link>
              </div>
            </div>
          </div>

          {/* Service 2 */}
          <div className="col s12 m6 l4">
            <div className="card hoverable service-card">
              <div className="card-image">
                <img src="/images/custom-parts.jpg" alt="Custom Parts" />
                <span className="card-title">Custom Parts</span>
              </div>
              <div className="card-content">
                <p>Custom manufacturing of specialized parts and components for any application or industry.</p>
              </div>
              <div className="card-action">
                <Link to="/contact">Learn More</Link>
              </div>
            </div>
          </div>

          {/* Service 3 */}
          <div className="col s12 m6 l4">
            <div className="card hoverable service-card">
              <div className="card-image">
                <img src="/images/artistic-creations.jpg" alt="Artistic Creations" />
                <span className="card-title">Artistic Creations</span>
              </div>
              <div className="card-content">
                <p>Bring your artistic visions to life with detailed 3D printed sculptures, models, and decorative items.</p>
              </div>
              <div className="card-action">
                <Link to="/contact">Learn More</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Materials Section */}
      <div className="section">
        <h2 className="center-align">Materials We Use</h2>
        <div className="row">
          <div className="col s12 m6">
            <div className="card-panel z-depth-1 material-card">
              <div className="row valign-wrapper">
                <div className="col s3">
                  <img src="/images/materials.jpg" alt="PLA" className="circle responsive-img" />
                </div>
                <div className="col s9">
                  <h5>PLA</h5>
                  <p>Standard materials for most applications. Affordable, versatile, and available in various colors.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="col s12 m6">
            <div className="card-panel z-depth-1 material-card">
              <div className="row valign-wrapper">
                <div className="col s3">
                  <img src="/images/materials2.jpg" alt="PLA" className="circle responsive-img" />
                </div>
                <div className="col s9">
                  <h5>PETG</h5>
                  <p>Advanced materials for functional parts requiring durability, flexibility, and heat resistance.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="section">
        <div className="card-panel neon-card center-align z-depth-2">
          <h3>Ready to start your project?</h3>
          <p className="flow-text">Contact us today for a free consultation and quote.</p>
          <Link 
            to="/stl-generator" 
            className="btn-large waves-effect waves-light white" 
            style={{ color: 'var(--primary-color)' }}
            onClick={() => amplitude.track('Button Clicked', {
              button: 'Generate Your Own STL',
              location: 'Home Call to Action'
            })}
          >
            Generate Your Own STL
          </Link>
        </div>
      </div>
    </>
  );
};

export default Home;
